// Strona główna (sesja 37 po rundzie R3, hero z rundy 47, sesja A — paczka H47).
//
// Droga do jednej akcji, w tej kolejności: HERO 47-A na jednej ciemnej scenie pod nagłówkiem — część 1
// „parabola fanbase” (H1 i podtytuł właściciela, grafika: utwór → paczki editów → nowe konta, akcja
// z mikrotekstem) i część 2 „GENERATOR FANOWSKICH EDITÓW” (trzy kroki z grafiką), pod nimi pokaz trzech
// editów z 37-R3 bez zmian, koszt w tokenach z linią pieniędzy, sekcja zamykająca z rzędem faktów
// i stopka w trzech grupach.
//
// STRONA PRZEWIJA SIĘ ZWYCZAJNIE (właściciel 27.09: „scroll nie ma lockować scrollowania STRONY”): hero nie
// jest przypięte, nikt nie przejmuje kółka ani dotyku, nagłówek przykleja się jak na innych stronach
// publicznych. Ruch obu części zależy od czasu od wejścia w widok i siada w klatce spoczynku równej
// pierwszemu malowaniu (`hooks/useHeroMotion.ts`, `hooks/useGeneratorSequence.ts`). Nadtytuł, zdanie pod
// hasłem D, cichy link do trzech kroków, druga mapa z lotu ptaka i sekcja `#trzy-kroki` odeszły razem
// z hero D: część 2 niesie trzy kroki właściciela.
//
// NA 390 JEST JESZCZE JEDNA DROGA: dolny pasek akcji, który wchodzi dopiero wtedy,
// gdy przycisk hero zjedzie z ekranu, i chowa się przy sekcji zamykającej, która ma
// własną akcję. NIGDY nie staje razem z paskiem cookie — to była pułapka rundy R2:
// dwa paski przy dolnej krawędzi zasłaniają się nawzajem, a ten u dołu jest gatem
// prawnym. Kiedy pasek jest widoczny, akcja nagłówka przechodzi w obrys, żeby na
// ekranie stał JEDEN pełny przycisk.
//
// Dane przykładowe (nicki kont, liczby editów w paczkach) są STAŁĄ W KODZIE, nie słownikiem
// — tak samo jak trzy edity sceny w `LandingEditShowcase.tsx` (MAPA 37, „Nowe teksty”).
// Liczby są prawdziwe: premia startowa wychodzi z `lib/startBonus.ts` (czyli z katalogu
// w `app/billing/costs.py`), a cena w linii pieniędzy z `/api/billing/plans` — z tego
// samego katalogu, z którego liczy Cennik. Bez odpowiedzi linia pieniędzy nie powstaje.

import { useRef, useState } from 'react'
import { Link } from 'react-router'
import { useI18n } from '../lib/i18n'
import { usePublicPath } from '../lib/publicPaths'
import { usePlans } from '../hooks/useBilling'
import { price as money } from '../lib/money'
import { editWord } from '../locales/plural'
import { TokenAmount } from '../ds/primitives/TokenAmount'
import { FREE_START_EDITS } from '../lib/startBonus'
import Icon from '../components/Icon'
import Seo from '../components/Seo'
import CookieBar from '../components/CookieBar'
import LandingEditShowcase from '../components/LandingEditShowcase'
import HeroParabola from '../components/public/HeroParabola'
import HeroGenerator from '../components/public/HeroGenerator'
import { Glyph37 } from '../components/public/glyphs37'
import type { Glyph37Name } from '../components/public/glyphs37'
import PublicHeader from '../components/public/PublicHeader'
import { useGone } from '../hooks/useGone'
import PublicFooterBar from '../components/public/PublicFooterBar'
import { useHeroMedia } from '../hooks/useHeroMotion'
import '../styles/minimal-public.css'

/**
 * Stawka tokenów za akcję (reguła 34, decyzja Domiego 20.09): 1 edit = 1 token,
 * 1 publikacja = 1 token.
 *
 * Liczby stoją TUTAJ, a nie przychodzą z `/api/billing/plans`, bo katalog backendu
 * nadal liczy w gemach po starych stawkach — przejście backendu na tokeny to osobna
 * pozycja („do backendu po akceptacji”, brief paczki 37b), a strona publiczna ma
 * pokazywać cennik, który właściciel zatwierdził. Gdy backend przejdzie, te dwie
 * stałe znikają na rzecz `plans.data.gem_costs`.
 */
const TOKENS_PER_EDIT = 1
const TOKENS_PER_PUBLISH = 1

/** Pięć faktów sekcji zamykającej: glif z arkusza sesji + jedno zdanie. */
const FACTS: { g: Glyph37Name; k: string }[] = [
  { g: 'nocard', k: 'fact1' },
  { g: 'lock', k: 'fact2' },
  { g: 'key', k: 'fact3' },
  { g: 'shield', k: 'fact4' },
  { g: 'calx', k: 'fact5' },
]

/**
 * „Plany od {price} / mies.” z kwotą jako DRUGĄ liczbą zdania (reguła 25: mono 32/600).
 *
 * Zdanie rozcinamy po znaczniku podstawionym w miejsce `{price}`, a nie po samej kwocie:
 * kwota bywa podciągiem reszty zdania („od 29,99 €” w innym języku mogłaby się powtórzyć),
 * a znacznik U+241F nie występuje w żadnym tekście, który ktokolwiek napisze.
 */
const MONEY_SLOT = '␟'

function MoneyLine({ from, t }: { from: string; t: (k: string, v?: Record<string, string | number>) => string }) {
  const [before, after = ''] = t('landing.price.from', { price: MONEY_SLOT }).split(MONEY_SLOT)
  return <p className="mp-money">{before}<b className="mp-money-num">{from}</b>{after}</p>
}

export default function Landing() {
  const { t, locale } = useI18n()
  const pub = usePublicPath()
  const plans = usePlans()
  const closing = useRef<HTMLDivElement>(null)
  // Ruch dozwolony i orientacja świata paraboli (v < 600 ≤ w < 1336 ≤ h). Czytane synchronicznie, żeby
  // pierwszy render klienta narysował to samo pole co statyczny HTML.
  const { motion, orient } = useHeroMedia()
  const closingAway = useGone(closing)
  // Nagłówek wypełnia swoją akcję dopiero, gdy żaden wypełniony przycisk tej samej drogi
  // nie stoi na ekranie (VB-10): pomiar na SAMYCH przyciskach.
  const heroCta = useRef<HTMLAnchorElement>(null)
  const closingCta = useRef<HTMLAnchorElement>(null)
  const heroCtaGone = useGone(heroCta)
  const closingCtaGone = useGone(closingCta)
  // Pasek cookie ma pierwszeństwo przy dolnej krawędzi: jest gatem prawnym, a nie
  // propozycją. Stan przychodzi z samego paska, bo człowiek może go zamknąć przy
  // otwartej stronie — i wtedy pasek akcji ma wejść, a nie czekać na przeładowanie.
  const [cookieOnScreen, setCookieOnScreen] = useState(false)
  // Próg dolnego paska akcji liczony z przycisku hero (brief H47, B9).
  const ctaBar = heroCtaGone && closingAway && !cookieOnScreen

  const micro = t('landing.hero.micro', { n: FREE_START_EDITS, edits: editWord(locale, FREE_START_EDITS) })

  // Linia pieniędzy: najtańszy plan, który da się kupić. Bez katalogu nie ma linii —
  // „od 0 €” i „od —” to dwa sposoby na powiedzenie nieprawdy o cenie.
  const cheapest = (plans.data?.plans ?? [])
    .filter((p) => p.purchasable && p.price_cents > 0)
    .sort((a, b) => a.price_cents - b.price_cents)[0] ?? null
  const from = cheapest ? money(cheapest.price_cents, plans.data?.currency ?? 'EUR', locale) : null

  return (
    <div className={`mp${ctaBar ? ' has-cta-bar' : ''}`}>
      <Seo page="landing" />

      {/* Nagłówek w osobnym ciemnym pasie: scena hero leży w <main>, nagłówek nie. */}
      <div className="mp-dark mp-hp-top">
        <PublicHeader onStage quietCta={!heroCtaGone || !closingCtaGone} />
      </div>

      <main>
        <div className="mp-dark mp-hp-scena">
          <HeroParabola orient={orient} motion={motion} ctaRef={heroCta} micro={micro} />
          <HeroGenerator wide={orient === 'h'} motion={motion} />
          {/* Pokaz trzech editów z 37-R3 pod częścią 2, bez zmian: klikalne edity z odtwarzaczem to
              dowód produktu. Bez nagłówka i bez nowego zdania. */}
          <div className="mp-hp-show">
            <section aria-label={t('landing.showcase.title')}>
              <LandingEditShowcase />
            </section>
          </div>
        </div>

        <section className="mp-sec mp-sec-line" aria-labelledby="landing-cost-heading">
          <div className="mp-price">
            <div>
              <h2 id="landing-cost-heading">{t('landing.price.heading')}</h2>
              <p>{t('landing.price.refund')}</p>
              {from ? <MoneyLine from={from} t={t} /> : null}
              <Link to={pub('/pricing')} className="mn-lnk">
                {t('landing.price.link')}<Icon name="ArrowRight" size={16} />
              </Link>
            </div>
            <div className="mp-price-nums">
              <div>
                <span>{t('landing.price.oneEdit')} =</span>
                {/* N11 (P1-04, reguła 48): glif i liczba, bez słowa na ekranie; słowo słyszy czytnik. */}
                <b><TokenAmount locale={locale} n={TOKENS_PER_EDIT} place="balance" /></b>
              </div>
              <div>
                <span>{t('landing.price.onePublish')} =</span>
                <b><TokenAmount locale={locale} n={TOKENS_PER_PUBLISH} place="balance" /></b>
              </div>
            </div>
          </div>
        </section>

        {/* Sekcja zamykająca: jedna akcja i rząd faktów, z których każdy da się
            sprawdzić na tej samej stronie (artboard 01, „Pierwsza seria jest za darmo.”). */}
        <div className="mp-dark mp-closing" ref={closing}>
          <section className="mp-sec" aria-labelledby="landing-closing-heading">
            <h2 id="landing-closing-heading">{t('landing.closing.heading')}</h2>
            <div className="mp-closing-cta">
              <Link to="/register" className="btn btn-primary" data-cta="zamkniecie" ref={closingCta}>{t('landing.nav.startFree')}</Link>
              <small>{micro}</small>
            </div>
            <ul className="mp-facts">
              {FACTS.map((f) => (
                <li key={f.k}>
                  <Glyph37 name={f.g} size={18} />
                  <span>{t(`landing.closing.${f.k}`)}</span>
                </li>
              ))}
            </ul>
          </section>
        </div>
      </main>

      <PublicFooterBar />
      {/* Pasek cookie żyje na stronie treści, nie w powłoce aplikacji: to jedyny
          ekran, na którym artboard go rysuje, a jego wysokość rezerwuje scena. */}
      <CookieBar onVisible={setCookieOnScreen} />
      {ctaBar ? (
        <div className="mp-cta-bar">
          <Link to="/register" className="btn btn-primary" data-cta="pasek">{t('landing.nav.startFree')}</Link>
          <small>{micro}</small>
        </div>
      ) : null}
    </div>
  )
}
